// External Packages
import { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";

// Internal Packages
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";

// External Components
import { MdBarChart, MdRefresh } from "react-icons/md";

// Internal Components
import SearchBar from "../UI/SearchBar";
import DateFilter from "../UI/DateFilter";
import Heading from "../UI/Heading";
import FilterMenu from "../UI/FilterMenu";
import PaginationMenu from "../UI/PaginationMenu";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import ExportCsvButton from "../UI/ExportCsvButton";
import DomainSwitch from "../UI/DomainSwitch";
import { isoDateToString } from "../../util/format-date";
import { endOfWeek, startOfWeek } from "date-fns";
/** ========================================================================
 * * Leads Page
 * Renders a page, which displays all leads.
 * ========================================================================== */

export default function LeadsPage() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filters
  const defaultFilters = {
    query: "",
    scope: "All",
    status: "All",
    stage: "All",
    source: "All",
    sd: startOfWeek(new Date()).toISOString(), // Start of the week
    ed: endOfWeek(new Date()).toISOString(), // End of the week
    limit: "50", 
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  // Initialize States
  const [opportunities, setOpportunities] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      const data = await requestAdminAPI("fetch-opportunities", {
        ...filter,
      });
      // Replace URL
      let url = "/opportunities?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setOpportunities(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== FILTER OPTIONS ===================
  const filterOptions = [
    {
      label: "Source",
      options: [
        "All",
        "Account Created",
        "Callback Request",
        "Visit Appointment",
        "Meta Facebook Lead",
      ],
      selected: filter.source,
    },
    {
      label: "Stage",
      options: [
        "All",
        { value: 1, label: "New Lead" },
        { label: "Nurture", value: 2 },
        { label: "Booked", value: 3 },
        { label: "Subscribed", value: 4 },
        { label: "Abandoned", value: 5 },
      ],
      selected: filter.stage,
    },
    {
      label: "Status",
      options: ["All", "Open", "Won", "Lost", "Abandoned"],
      selected: filter.status,
    },
  ];

  // Apply Filters
  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container grid">
      <Heading title="Opportunities" isBackArrow={true}>
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
          defaultPeriod="Week"
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <DomainSwitch
          on={filter.scope !== "All"}
          onSwitch={(scope) => setFilter({ ...filter, scope })}
        />
        <MdBarChart
          onClick={() => navigate("/opportunities/report")}
          className="icon"
          title="Report"
        />
        <MdRefresh onClick={fetchRecords} className="icon" title="Refresh" />
        <ExportCsvButton array={opportunities} filename="opportunities" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={5} max={8} />
      ) : opportunities.length > 0 ? (
        <div className="list">
          {opportunities.map((opportunity, idx) => (
            <div
              key={idx}
              className="grid pill lg:grid-cols-8"
              onClick={(e) =>
                navigate(`/opportunities/info?id=${opportunity.id}`, e)
              }
            >
              <p className="text-stone-400">
                {isoDateToString(opportunity.createdAt)}
              </p>
              <p className="font-semibold">{opportunity.name}</p>
              <p>{opportunity.contact?.phone}</p>
              <p className="hidden lg:col-span-2 lg:block">
                {opportunity.contact?.email}
              </p>
              <p className="hidden lg:block text-stone-400">
                {opportunity.source}
              </p>
              <p className="hidden font-semibold lg:block">
                {opportunity.employee?.name}
              </p>
              <p className="hidden mx-auto text-stone-400 lg:block ">
                {opportunity.stage?.name}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={filter.limit}
        setLimit={(limit) => setFilter({ ...filter, limit })}
        loading={loading}
        count={opportunities.length}
      />
    </div>
  );
}
